'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  useActiveLeague,
  useUpcomingMatches,
  useUserFootballScore,
  useLeagueLeaderboard,
  useUserRaffleWinnings,
  useMyLeaguePredictions,
  useSubmitLeaguePrediction,
  useSubmitLeaguePredictionsBatch,
  useLeagueDayGoalsStatus,
  useSubmitLeagueDayGoals,
} from '../hooks/useFootball'
import type { FootballGameMode, FootballMatch } from '../types'
import LuckyIcon from '../components/lucky/LuckyIcon'
import { TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import { format } from 'date-fns'
import { useT } from '../contexts/LocaleContext'
import { adFailMessage, preloadRewardedAd, showRewardedAd } from '../lib/ads'

type PredictionChoice = 'home' | 'away' | 'draw'
type ContentTab = 'matches' | 'leaderboard' | 'winnings'

export default function FootballPage() {
  const t = useT()
  const [mode, setMode] = useState<FootballGameMode>('league')
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get('match')) setMode('big_match')
  }, [searchParams])

  const modes: { mode: FootballGameMode; label: string; hint: string }[] = [
    { mode: 'league', label: t('football.modeLeague'), hint: t('football.hintLeague') },
    { mode: 'big_match', label: t('football.modeBig'), hint: t('football.hintBig') },
  ]

  return (
    <div className="lucky-fb mini-app-container pb-20 min-h-screen">
      <div className="lucky-fb__inner">
        <header className="lucky-fb__header">
          <div className="lucky-fb__header-icon">
            <LuckyIcon name="world" size={36} alt="" />
          </div>
          <div>
            <h1>{t('football.title')}</h1>
            <p>{t('football.subtitle')}</p>
          </div>
        </header>

        <div className="lucky-fb__modes">
          {modes.map((m) => (
            <button
              key={m.mode}
              type="button"
              className={`lucky-fb__mode ${mode === m.mode ? 'is-on' : ''}`}
              onClick={() => setMode(m.mode)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <ModeContent key={mode} mode={mode} modeHint={modes.find((m) => m.mode === mode)?.hint || ''} />
      </div>
    </div>
  )
}

function ModeContent({ mode, modeHint }: { mode: FootballGameMode; modeHint: string }) {
  const navigate = useNavigate()
  const t = useT()
  const isBigMatch = mode === 'big_match'
  const [tab, setTab] = useState<ContentTab>('matches')
  const [submittingMatchId, setSubmittingMatchId] = useState<number | null>(null)
  const [submittingBatch, setSubmittingBatch] = useState(false)
  const [leagueDraft, setLeagueDraft] = useState<Record<number, PredictionChoice[]>>({})
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const teamParam = Number(searchParams.get('team') || 0)
    if (teamParam > 0) {
      navigate(`/football/dream-teams/${teamParam}`, { replace: true })
      return
    }
    if (searchParams.get('match')) {
      navigate('/football/dream-teams/explore', { replace: true })
    }
  }, [searchParams, navigate])

  const { data: leagueData } = useActiveLeague(mode)
  const league = leagueData?.league

  const { data: matchesData, isLoading: matchesLoading } = useUpcomingMatches(50, mode)
  const matches = matchesData?.matches || []

  const { data: scoreData } = useUserFootballScore(league?.id || 0)
  const userScore = scoreData?.score

  const { data: leaderboardData } = useLeagueLeaderboard(league?.id || 0, 10)
  const leaderboard = leaderboardData?.leaderboard || []

  const { data: winningsData } = useUserRaffleWinnings(league?.id || 0)
  const winnings = winningsData?.winnings || []

  const { data: myPredictionsData } = useMyLeaguePredictions()
  const myPredictions = myPredictionsData?.predictions || []

  const submitPrediction = useSubmitLeaguePrediction()
  const submitBatch = useSubmitLeaguePredictionsBatch()
  const { data: dayGoalsStatus, refetch: refetchDayGoals } = useLeagueDayGoalsStatus(
    !isBigMatch && league?.id ? league.id : 0,
  )
  const submitDayGoals = useSubmitLeagueDayGoals()
  const [homeGoalsGuess, setHomeGoalsGuess] = useState('')
  const [awayGoalsGuess, setAwayGoalsGuess] = useState('')
  const [submittingDayGoals, setSubmittingDayGoals] = useState(false)
  const [dayGoalsAdReady, setDayGoalsAdReady] = useState(false)
  const [watchingDayGoalsAd, setWatchingDayGoalsAd] = useState(false)

  useEffect(() => {
    if (isBigMatch || !dayGoalsStatus?.eligible) return
    void preloadRewardedAd()
  }, [isBigMatch, dayGoalsStatus?.eligible])

  useEffect(() => {
    setDayGoalsAdReady(false)
  }, [league?.id, dayGoalsStatus?.match_date])

  const myChoicesByMatch = useMemo(() => {
    const map = new Map<number, PredictionChoice[]>()
    myPredictions.forEach((p) => {
      const choices =
        p.choices && p.choices.length > 0
          ? (p.choices as PredictionChoice[])
          : p.choice
            ? [p.choice as PredictionChoice]
            : []
      map.set(p.match_id, choices)
    })
    return map
  }, [myPredictions])

  const openLeagueMatches = useMemo(() => {
    if (isBigMatch) return []
    return matches.filter(
      (m) => m.status === 'upcoming' && !(myChoicesByMatch.get(m.id)?.length),
    )
  }, [isBigMatch, matches, myChoicesByMatch])

  const leagueDraftComplete =
    openLeagueMatches.length > 0 &&
    openLeagueMatches.every((m) => (leagueDraft[m.id]?.length || 0) > 0)

  const toggleLeagueDraft = (matchId: number, choice: PredictionChoice) => {
    setLeagueDraft((prev) => {
      const current = prev[matchId] || []
      const next = current.includes(choice)
        ? current.filter((c) => c !== choice)
        : [...current, choice]
      return { ...prev, [matchId]: next }
    })
  }

  const handlePredict = async (matchId: number, choices: PredictionChoice[]) => {
    if (choices.length === 0) return
    setSubmittingMatchId(matchId)
    try {
      await submitPrediction.mutateAsync({ matchId, choices })
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || t('football.errSubmitPrediction'))
    } finally {
      setSubmittingMatchId(null)
    }
  }

  const handleLeagueBatchSubmit = async () => {
    if (!leagueDraftComplete) {
      alert(t('football.pickRequired'))
      return
    }
    setSubmittingBatch(true)
    try {
      await submitBatch.mutateAsync(
        openLeagueMatches.map((m) => ({
          match_id: m.id,
          choices: leagueDraft[m.id],
        })),
      )
      setLeagueDraft({})
      void refetchDayGoals()
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || t('football.errSubmitPoll'))
    } finally {
      setSubmittingBatch(false)
    }
  }

  const handleDayGoalsWatchAd = async () => {
    if (!dayGoalsStatus?.eligible || watchingDayGoalsAd || dayGoalsAdReady) return
    setWatchingDayGoalsAd(true)
    try {
      const ad = await showRewardedAd()
      if (!ad.ok) {
        alert(adFailMessage(ad.reason))
        return
      }
      setDayGoalsAdReady(true)
    } finally {
      setWatchingDayGoalsAd(false)
    }
  }

  const handleDayGoalsSubmit = async (payment: 'bronze' | 'ads') => {
    if (!league?.id || !dayGoalsStatus?.match_date || !dayGoalsStatus.eligible) return
    if (payment === 'ads' && !dayGoalsAdReady) return
    const home = Number(homeGoalsGuess)
    const away = Number(awayGoalsGuess)
    if (!Number.isInteger(home) || !Number.isInteger(away) || home < 0 || away < 0) {
      alert(t('football.dayGoalsInvalid'))
      return
    }
    setSubmittingDayGoals(true)
    try {
      await submitDayGoals.mutateAsync({
        leagueId: league.id,
        matchDate: dayGoalsStatus.match_date,
        homeTotal: home,
        awayTotal: away,
        payment,
      })
      setHomeGoalsGuess('')
      setAwayGoalsGuess('')
      setDayGoalsAdReady(false)
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || t('football.dayGoalsErr'))
    } finally {
      setSubmittingDayGoals(false)
    }
  }

  return (
    <>
      <section className="lucky-fb__card">
        {modeHint && <p className="lucky-fb__hint">{modeHint}</p>}

        {league && (
          <div className="lucky-fb__league-row">
            <span className="lucky-fb__league-name">
              {isBigMatch ? t('football.modeBig') : t('football.dailyLeague')}
            </span>
            <span className={`lucky-fb__tag ${isBigMatch ? 'lucky-fb__tag--big' : 'lucky-fb__tag--league'}`}>
              {isBigMatch ? t('common.bigMatch') : t('common.league')}
            </span>
          </div>
        )}

        {userScore && (
          <div className="lucky-fb__scores">
            <div>
              <span>{t('football.totalScore')}</span>
              <strong className="is-gold">{userScore.total_score}</strong>
            </div>
            <div>
              <span>{t('common.daily')}</span>
              <strong className="is-green">{userScore.daily_score}</strong>
            </div>
            <div>
              <span>{t('common.monthly')}</span>
              <strong className="is-blue">{userScore.monthly_score}</strong>
            </div>
          </div>
        )}
      </section>

      <div className="lucky-fb__tabs">
        <TabButton active={tab === 'matches'} onClick={() => setTab('matches')}>
          {t('football.tabMatches')}
        </TabButton>
        <TabButton active={tab === 'leaderboard'} onClick={() => setTab('leaderboard')}>
          {t('football.tabLeaderboard')}
        </TabButton>
        {isBigMatch && (
          <TabButton active={false} onClick={() => navigate('/football/dream-teams/explore')}>
            {t('football.tabDreamTeams')}
          </TabButton>
        )}
        <TabButton active={tab === 'winnings'} onClick={() => setTab('winnings')}>
          {t('football.tabWinnings')}
        </TabButton>
      </div>

      {tab === 'matches' && (
        <div className="lucky-fb__list">
          {matchesLoading ? (
            <div className="lucky-fb__empty">{t('football.loadingMatches')}</div>
          ) : matches.length === 0 ? (
            <div className="lucky-fb__empty">
              {isBigMatch
                ? t('football.noBigMatch')
                : league
                  ? t('football.noLeagueMatch')
                  : t('football.noActiveLeague')}
            </div>
          ) : (
            <>
              {matches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  isBigMatch={isBigMatch}
                  myChoices={myChoicesByMatch.get(match.id)}
                  draftChoices={isBigMatch ? undefined : leagueDraft[match.id]}
                  isPending={submittingMatchId === match.id || submittingBatch}
                  onPredict={handlePredict}
                  onToggleDraft={isBigMatch ? undefined : toggleLeagueDraft}
                />
              ))}
              {!isBigMatch && openLeagueMatches.length > 0 && (
                <div className="lucky-fb__sticky">
                  <p>
                    {leagueDraftComplete
                      ? t('football.readyBatch', { n: openLeagueMatches.length })
                      : t('football.draftHint', { n: openLeagueMatches.length })}
                  </p>
                  <button
                    type="button"
                    className="lucky-fb__cta"
                    disabled={!leagueDraftComplete || submittingBatch}
                    onClick={handleLeagueBatchSubmit}
                  >
                    {submittingBatch ? t('football.submitting') : t('football.submitPredictions')}
                  </button>
                </div>
              )}
            </>
          )}
          {!isBigMatch &&
            !matchesLoading &&
            matches.length > 0 &&
            (dayGoalsStatus?.eligible || dayGoalsStatus?.submitted) && (
            <section className="lucky-fb__card lucky-fb__day-goals">
              <h3 className="lucky-fb__section-title">{t('football.dayGoalsTitle')}</h3>
              <p className="lucky-fb__hint">
                {t('football.dayGoalsHint', {
                  n: dayGoalsStatus.match_count || 0,
                  silver: dayGoalsStatus.reward_silver || 1,
                })}
              </p>
              {dayGoalsStatus.submitted && dayGoalsStatus.prediction ? (
                <p className="lucky-fb__picked">
                  {t('football.dayGoalsSubmitted', {
                    home: dayGoalsStatus.prediction.predicted_home_total,
                    away: dayGoalsStatus.prediction.predicted_away_total,
                  })}
                </p>
              ) : (
                <>
                  <div className="lucky-fb__day-goals-inputs">
                    <label>
                      <span>{t('football.dayGoalsHome')}</span>
                      <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={homeGoalsGuess}
                        onChange={(e) => setHomeGoalsGuess(e.target.value)}
                        disabled={submittingDayGoals}
                      />
                    </label>
                    <label>
                      <span>{t('football.dayGoalsAway')}</span>
                      <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={awayGoalsGuess}
                        onChange={(e) => setAwayGoalsGuess(e.target.value)}
                        disabled={submittingDayGoals}
                      />
                    </label>
                  </div>
                  <div className="lucky-fb__day-goals-actions">
                    <button
                      type="button"
                      className="lucky-fb__cta lucky-fb__cta--bronze"
                      disabled={submittingDayGoals || watchingDayGoalsAd}
                      onClick={() => void handleDayGoalsSubmit('bronze')}
                    >
                      {submittingDayGoals
                        ? t('football.submitting')
                        : t('football.dayGoalsPayBronze', {
                            n: dayGoalsStatus.fee_bronze || 1,
                          })}
                    </button>
                    {dayGoalsAdReady ? (
                      <button
                        type="button"
                        className="lucky-fb__cta lucky-fb__cta--ad"
                        disabled={submittingDayGoals}
                        onClick={() => void handleDayGoalsSubmit('ads')}
                      >
                        {submittingDayGoals
                          ? t('football.submitting')
                          : t('football.dayGoalsSubmitAfterAd')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="lucky-fb__cta lucky-fb__cta--ad"
                        disabled={submittingDayGoals || watchingDayGoalsAd}
                        onClick={() => void handleDayGoalsWatchAd()}
                      >
                        {watchingDayGoalsAd
                          ? t('common.watchingAd')
                          : t('football.dayGoalsPayAd')}
                      </button>
                    )}
                  </div>
                  {dayGoalsAdReady && (
                    <p className="lucky-fb__hint">{t('football.dayGoalsAdReadyHint')}</p>
                  )}
                </>
              )}
            </section>
          )}
        </div>
      )}

      {tab === 'leaderboard' && (
        <section className="lucky-fb__card">
          <h3 className="lucky-fb__section-title">
            <TrophyGlyph size={22} />
            {t('football.topOf', { mode: isBigMatch ? t('common.bigMatch') : t('common.league') })}
          </h3>
          {leaderboard.length === 0 ? (
            <p className="lucky-fb__empty">{t('football.noScore')}</p>
          ) : (
            leaderboard.map((entry, index) => (
              <div key={entry.id} className="lucky-fb__rank-row">
                <span className="lucky-fb__rank-num">#{index + 1}</span>
                {entry.user?.photo_url ? (
                  <img src={entry.user.photo_url} alt="" className="lucky-fb__avatar" />
                ) : (
                  <div className="lucky-fb__avatar lucky-fb__avatar--fallback">
                    {entry.user?.first_name?.[0] || '?'}
                  </div>
                )}
                <div className="lucky-fb__rank-text">
                  <strong>
                    {entry.user?.first_name} {entry.user?.last_name}
                  </strong>
                  <span>@{entry.user?.username || t('common.noName')}</span>
                </div>
                <div className="lucky-fb__rank-score">
                  <strong>{entry.total_score}</strong>
                  <span>
                    {t('football.dayMonth', { day: entry.daily_score, month: entry.monthly_score })}
                  </span>
                </div>
              </div>
            ))
          )}
        </section>
      )}

      {tab === 'winnings' && (
        <section className="lucky-fb__card">
          <h3 className="lucky-fb__section-title">
            <LuckyIcon name="trophy" size={22} alt="" />
            {t('football.wonPrizes')}
          </h3>
          {winnings.length === 0 ? (
            <p className="lucky-fb__empty">{t('football.noWinnings')}</p>
          ) : (
            winnings.map((w) => (
              <div key={w.id} className="lucky-fb__win-row">
                <div>
                  <strong>{w.prize_type === 'gold' ? t('football.goldCoin') : t('football.silverCoin')}</strong>
                  <span>{drawTypeLabel(w.draw_type, t)}</span>
                </div>
                <em>{w.awarded_at}</em>
              </div>
            ))
          )}
        </section>
      )}
    </>
  )
}

function drawTypeLabel(drawType: string, t: (key: string) => string) {
  const key = `football.draws.${drawType}`
  const label = t(key)
  return label === key ? drawType : label
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className={`lucky-fb__tab ${active ? 'is-on' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function outcomeLabel(choice: PredictionChoice, t: (key: string) => string) {
  if (choice === 'home') return t('football.outcomeHome')
  if (choice === 'away') return t('football.outcomeAway')
  return t('football.outcomeDraw')
}

function MatchCard({
  match,
  isBigMatch,
  myChoices,
  draftChoices,
  isPending,
  onPredict,
  onToggleDraft,
}: {
  match: FootballMatch
  isBigMatch: boolean
  myChoices?: PredictionChoice[]
  draftChoices?: PredictionChoice[]
  isPending: boolean
  onPredict: (matchId: number, choices: PredictionChoice[]) => void
  onToggleDraft?: (matchId: number, choice: PredictionChoice) => void
}) {
  const navigate = useNavigate()
  const t = useT()
  const isFinished = match.status === 'finished'
  const hasSubmitted = !!myChoices && myChoices.length > 0
  const canPredict = match.status === 'upcoming' && !hasSubmitted
  const allowMulti = !isBigMatch
  const selected = hasSubmitted ? myChoices! : draftChoices || []

  const statusLabel =
    match.status === 'live' ? t('football.statusLive') : isFinished ? t('football.statusFinished') : t('football.statusUpcoming')
  const statusClass =
    match.status === 'live' ? 'is-live' : isFinished ? 'is-done' : 'is-soon'

  return (
    <article className="lucky-fb__match">
      <div className="lucky-fb__match-top">
        <div className="lucky-fb__teams">
          {match.home_club?.flag_url ? (
            <img src={match.home_club.flag_url} alt="" className="lucky-fb__flag" />
          ) : (
            <span className="lucky-fb__flag lucky-fb__flag--empty" aria-hidden />
          )}
          <div className="lucky-fb__team-names">
            <strong>{match.home_team}</strong>
            <strong>{match.away_team}</strong>
          </div>
          {match.away_club?.flag_url ? (
            <img src={match.away_club.flag_url} alt="" className="lucky-fb__flag" />
          ) : (
            <span className="lucky-fb__flag lucky-fb__flag--empty" aria-hidden />
          )}
        </div>
        <span className={`lucky-fb__status ${statusClass}`}>{statusLabel}</span>
      </div>

      <div className="lucky-fb__match-meta">
        <span>{format(new Date(match.start_time), 'yyyy/MM/dd HH:mm')}</span>
        {isFinished && match.home_score !== null && match.away_score !== null && (
          <span className="lucky-fb__scoreline">
            {match.home_score} – {match.away_score}
          </span>
        )}
      </div>

      {hasSubmitted ? (
        <p className="lucky-fb__picked">
          {allowMulti ? t('football.yourPoll') : t('football.yourPrediction')}:{' '}
          {myChoices!.map((c) => outcomeLabel(c, t)).join(' · ')}
          {` ${t('football.pickedSuffix', { team: match.home_team })}`}
        </p>
      ) : (
        <p className="lucky-fb__pick-hint">
          {t('football.pickHint', { team: match.home_team })}
        </p>
      )}

      <div className="lucky-fb__choices">
        {(['home', 'draw', 'away'] as PredictionChoice[]).map((choice) => {
          const active = selected.includes(choice)
          return (
            <button
              key={choice}
              type="button"
              className={`lucky-fb__choice ${active ? 'is-on' : ''}`}
              disabled={!canPredict || isPending}
              onClick={() => {
                if (!canPredict) return
                if (allowMulti && onToggleDraft) {
                  onToggleDraft(match.id, choice)
                } else {
                  onPredict(match.id, [choice])
                }
              }}
            >
              {outcomeLabel(choice, t)}
            </button>
          )
        })}
      </div>

      {!canPredict && !hasSubmitted && (
        <p className="lucky-fb__closed">
          {allowMulti ? t('football.pollClosed') : t('football.predictionClosed')}
        </p>
      )}

      {isBigMatch && (
        <button
          type="button"
          className="lucky-fb__dream"
          onClick={() => navigate(`/football/dream-team/${match.id}`)}
        >
          {t('football.buildDreamTeam')}
        </button>
      )}
    </article>
  )
}
